import type { Accidental, LetterName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import KeySignature from '../uTheory.Notation/keySignature';
import Note from '../uTheory.Notation/note';
import { toHTML } from '../uTheory.Font/font';
import NoteHolder from '../uTheory.Notation/noteHolder';

export interface LastSharpOrFlatQuestion {
  /* html key sig name */
  keySignatureHTML: string;
  keySignature: KeySignature;
  lastAccidentalNote: Note;
  skills: string[];
  lastSharpOrFlat:
    | {
        letter: string;
        accidental: string;
        text: 'sharp' | 'flat';
      }
    | undefined;
  text: string[];
  questionString: string;
}

export default class KeyByLastSharpOrFlat extends InteractivePrototype {
  answer: {
    letter: string;
    accidental: Accidental;
  } = {
    letter: '',
    accidental: '',
  };

  declare questions: LastSharpOrFlatQuestion[];

  declare currentQuestion: LastSharpOrFlatQuestion;

  skillCalculator: SkillCalculator = new SkillCalculator();

  constructor(displayDiv: HTMLDivElement, options: InteractiveOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = KeyByLastSharpOrFlat.registry.skills;
    this.settings = {
      instructions: true,
      editor: false,
      notation: false,
      piano: false,
      letters: {
        letterButtons: true,
        customButtons: [
          { tagName: 'br' },
          { text: '#', value: '#', class: 'accidentals', hotKey: '#' },
          { text: 'n', value: '', class: 'accidentals', hotKey: 'n' },
          { text: 'b', value: 'b', class: 'accidentals', hotKey: 'b' },
        ],
        checkButton: true,
        trashButton: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.answer = {
      letter: '',
      accidental: '',
    };

    this.init(displayDiv, options);
  }

  generateQuestions(questions: string[] = this.options.questions) {
    this.questionPrompt =
      'If the <strong>last {question} is {question[2]}</strong><br /> the key is ' +
      "<strong><span id='keyByLastSharpOrFlatAnswerDiv'>__</span> {question[3]}</strong>";
    //        question.keyQuality;

    if (this.options.testMode !== true) {
      this.questionPrompt += '<br/> <small>Question {question[4]} of {question[5]}</small><br />';
    }

    this.questions =
      this.options.drill === true
        ? this.generateDrillQuestions()
        : this.generateManualQuestions(questions);
  }

  generateManualQuestions(questions = this.options.questions): LastSharpOrFlatQuestion[] {
    return questions
      .map((question, index) => KeyByLastSharpOrFlat.processQuestion(question, index, questions))
      .filter((q) => q) as LastSharpOrFlatQuestion[];
  }

  generateDrillQuestions(): LastSharpOrFlatQuestion[] {
    const generator = new QuestionGenerator();
    const maxQuestions = this.options.maxQuestions || 10;
    let generatedQuestions: string[] = generator.generate(
      this.options.skills,
      maxQuestions + 2,
      this.options
    );
    console.log(generatedQuestions);

    generatedQuestions = generatedQuestions
      .filter((q) => !(q === 'C/M' || q === 'A/m'))
      .slice(0, maxQuestions);

    return generatedQuestions.map((question, index) =>
      KeyByLastSharpOrFlat.processQuestion(question, index, generatedQuestions)
    ) as LastSharpOrFlatQuestion[];
  }

  static processQuestion(
    question: string,
    index: number = 0,
    array: string[] = []
  ): LastSharpOrFlatQuestion | false {
    if (question === 'C/M' || question === 'A/m') {
      return false;
    }
    const skillCalculator = new SkillCalculator();
    const questionParts = question.split('/');
    const keyNote = questionParts[0];
    const keyQuality = questionParts[1];
    const keySig = new KeySignature(keyNote, keyQuality);
    const lastSharpOrFlat = KeyByLastSharpOrFlat.getLastSharpOrFlat(keySig);
    const note = new Note({
      letter: lastSharpOrFlat.letter,
      accidental: lastSharpOrFlat.accidental,
    });
    if (!lastSharpOrFlat) return false;

    return {
      questionString: question,
      keySignatureHTML: keySig.toAccidentalFontString(),
      keySignature: keySig,
      lastAccidentalNote: note,
      skills: skillCalculator.getKeySignatureSkill(keySig),
      lastSharpOrFlat,
      text: [
        // 0: 'sharp' 'flat'
        lastSharpOrFlat.text,
        // 1: 'Bb Major'
        keySig.toAccidentalFontString(),
        // 2: 'Bb'
        note.toShortString(true),
        // 3: 'Major'
        keySig.quality,
        // 4: Question m
        `${index + 1}`,
        // 5: of m
        `${array.length}`,
      ],
    };
  }

  static getLastSharpOrFlat(key: KeySignature):
    | {
        letter: LetterName;
        accidental: Accidental;
        text: 'sharp' | 'flat';
      }
    | undefined {
    const accidentalsCount = key.accidentalsCount;
    const notes = key.collection;
    if (accidentalsCount > 0)
      return {
        letter: notes[7].letter,
        accidental: notes[7].accidental,
        text: 'sharp',
      };

    if (accidentalsCount < 0)
      return {
        letter: notes[4].letter,
        accidental: notes[4].accidental,
        text: 'flat',
      };

    return undefined;
  }

  newQuestion() {
    this.clearAnswer();
    super.newQuestion();
    this.displayAnswer();
  }

  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners() {
    this.letterPanel.addLetterCallback(this.addLetterToAnswer, this);
    this.letterPanel.addCustomButtonCallback(this.addAccidentalToAnswer, this);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  addLetterToAnswer(letter) {
    this.answer.letter = letter;
    this.displayAnswer();
  }

  addAccidentalToAnswer(accidental) {
    // if "trash" is called, delete what we have:
    if (accidental === 'trash') {
      if (this.answer.accidental) {
        this.answer.accidental = '';
      } else this.answer.letter = '';
    } else {
      this.answer.accidental = accidental;
    }
    this.displayAnswer();
  }

  displayAnswer() {
    const answerDiv = document.getElementById('keyByLastSharpOrFlatAnswerDiv');
    if (!this.answer.letter && !this.answer.accidental) {
      answerDiv.removeAttribute('aria-live');
      answerDiv.innerHTML = '__';
      return;
    }

    answerDiv.setAttribute('aria-live', 'polite');
    answerDiv.innerHTML = this.answer.letter + toHTML('accidental', this.answer.accidental);
  }

  checkAnswer() {
    const testMode = this.optionsTrue('testMode');
    if (!testMode && !this.answer.letter) {
      this.checkMark.warn('Click or type a letter name first.');
      return;
    }

    const rightAnswer = this.currentQuestion.keySignature.keyNote.toShortString();
    const userAnswer = this.answer.letter + this.answer.accidental;
    const correct = rightAnswer === userAnswer;

    if (testMode === true) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.getAnswerAndData(true);
      this.rightAnswer(2000);
      //        this.clearAnswer();
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  clearAnswer() {
    this.answer.letter = '';
    this.answer.accidental = '';
  }

  playAnswer() {
    this.player.stop();
    const scale = this.currentQuestion.keySignature.getScale();
    const tonic = scale[1].getNoteAt('-P8') as Note;

    const lowNotes = this.currentQuestion.keySignature.clone().setTonic(tonic).getScale();
    const highNotes = this.currentQuestion.keySignature.clone().setTonic(scale[8]).getScale();

    const isSharp = this.currentQuestion.lastAccidentalNote.accidental === '#';
    const isMajor = this.currentQuestion.keySignature.quality === 'Major';

    let notesArrays;
    if (isSharp && isMajor) {
      notesArrays = [
        [scale.M7 || scale[7]],
        [
          // notes["M7"] || notes[7],
          scale[4],
          scale[2],
          lowNotes[5],
        ],
        [scale[8], scale[3], scale[1], lowNotes[1]],
      ];
    } else if (!isSharp && isMajor) {
      notesArrays = [
        [scale[4]],
        [
          // notes[4],
          scale[2],
          lowNotes.M7 || lowNotes[7],
          lowNotes[5],
        ],
        [scale[3], scale[1], lowNotes[5], lowNotes[1]],
      ];
    } else if (isSharp && !isMajor) {
      notesArrays = [
        [highNotes[2]],
        [
          // notes["M7"] || notes[7],
          scale.M7,
          scale[4],
          lowNotes[5],
        ],
        [scale[8], scale[5], scale[3], lowNotes[1]],
      ];
    } else if (!isSharp && !isMajor) {
      notesArrays = [
        [scale[6]],
        [scale[5], scale[4], lowNotes.M7, lowNotes[5].getNoteAt('-P8')],
        [scale[5], scale[3], scale[1], lowNotes[1]],
      ];
    }

    const velocities = [0.9, 0.5, 0.3];
    const duration = 0.6;
    notesArrays.forEach((notes, i) => {
      const dur = !i && (isSharp || isMajor) ? duration * 2 : duration;
      const nh = new NoteHolder(notes);
      const velocity = velocities[i];
      this.player.play(nh, { startTime: dur * i, stopTime: dur * i + dur + 0.15, velocity });
    }, this);
  }

  getAnswerAndData(correct) {
    const question = this.currentQuestion;
    const userAnswer = this.answer.letter + this.answer.accidental;
    const sharpOrFlat = question.lastAccidentalNote.accidental === '#' ? 'sharp' : 'flat';
    const majorOrMinor = question.keySignature.quality;
    const questionText = `If the last ${sharpOrFlat} is ${question.lastAccidentalNote.toShortString()}, the key is ___ ${majorOrMinor}`;

    const correctAnswer = this.currentQuestion.keySignature.toString();
    const answerAndData = {
      question: this.currentQuestion.questionString,
      questionType: 'KeyByLastSharpOrFlat',
      questionText,
      questionSVG: null,
      studentAnswerText: `${userAnswer} ${majorOrMinor}`,
      studentAnswerSVG: null,
      correctAnswerText: correctAnswer,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    //      this.displayDiv.removeChild(this.answerDiv);
    super.gameOver();
  }

  static registry = {
    description: 'ID keys by their last accidental',
    skills: ['sharp_majors', 'sharp_minors', 'flat_majors', 'flat_minors'],
  };
}
